import { canEditCatalog } from "../utils/catalogEditPermissions";
import React, { useEffect, useState } from "react";
import { FiArrowLeft, FiArrowRight, FiBarChart2, FiEye, FiRefreshCw, FiSearch } from "react-icons/fi";
import { useSearchParams } from "react-router-dom";
import axios from "../../api/axiosInstance";
import { usePmsTheme } from "../../Components/PmsThemeContext";
import { readStoredUser } from "../../utils/sessionUser";
import useCrossTabDataRefreshToken from "../../hooks/useCrossTabDataRefresh";
import ItemMovementHistory from "../components/ItemMovementHistory";
import usePurchasingReadAccess from "../components/usePurchasingReadAccess";
import ItemAvailabilityControl, { ItemAvailabilityStatus, isItemAvailabilityLocked } from "../components/ItemAvailabilityControl";
import { reportsText } from "./reportsMessages";
import "../PurchasingUi.css";
import "./Reports.css";
import "./ItemLastPurchaseReport.css";

const PAGE_SIZE = 50;
const REPORT_VIEW = "purchasing.reports.items_purchased.view";

function purchaseDate(value, locale) {
  if (!value) return null;
  const dateOnly = String(value).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return null;
  const date = new Date(`${dateOnly}T00:00:00Z`);
  return Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== dateOnly
    ? null : date.toLocaleDateString(locale, { timeZone: "UTC" });
}

function daysSince(value) {
  if (!value) return null;
  const date = Date.parse(`${String(value).slice(0, 10)}T00:00:00Z`);
  if (!Number.isFinite(date)) return null;
  const today = new Date();
  const todayDate = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.max(0, Math.floor((todayDate - date) / 86400000));
}

function StockExpiration({ row, restricted, locale, t }) {
  const reportedStatus = restricted ? "restricted" : row.expirationStatus;
  const date = ["expired", "today", "dated"].includes(reportedStatus)
    ? purchaseDate(row.nextExpirationDate, locale) : null;
  const status = date ? reportedStatus : ({
    no_stock: "no_stock", not_stockable: "not_stockable", unsupported: "unsupported", restricted: "restricted",
  }[reportedStatus] || "unknown");
  const label = {
    expired: "Expired", today: "Expires today", dated: "Date recorded", unknown: "No known date",
    no_stock: "No stock", not_stockable: "Not stockable", unsupported: "Not available", restricted: "Limited access",
  }[status];
  return <div className="ilp-expiration">
    {date && <time dateTime={String(row.nextExpirationDate).slice(0, 10)}>{date}</time>}
    <span className={`ilp-expirationBadge ilp-expirationBadge--${status}`}>{t(label)}</span>
    {date && status === "dated" && Number.isInteger(row.daysToExpiration) && row.daysToExpiration > 0 &&
      <span className="ilp-expirationDetail">{t("Days remaining")}: {row.daysToExpiration.toLocaleString(locale)}</span>}
    {date && Number(row.unknownExpirationQuantity) > 0 &&
      <span className="ilp-expirationDetail">{t("Some stock has no date")}</span>}
  </div>;
}

export default function ItemLastPurchaseReport() {
  const { themeId, languageId } = usePmsTheme();
  const t = (text) => reportsText(text, languageId);
  const locale = languageId === "es" ? "es-ES" : "en-GB";
  const user = readStoredUser();
  const canEditItems = canEditCatalog(user, "item");
  const [searchParams] = useSearchParams();
  const businessUnitId = String(searchParams.get("business_unit_id") || user?.active_business_unit_id || "");
  const access = usePurchasingReadAccess(businessUnitId);
  const canView = access.can(REPORT_VIEW);
  const canViewMovements = access.can("purchasing.stock.current.movements");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [expiration, setExpiration] = useState("with");
  const [expirationScope, setExpirationScope] = useState(null);
  const restrictedExpiration = expirationScope?.contextKey === access.contextKey && expirationScope.value === "restricted_locations";
  const effectiveExpiration = restrictedExpiration ? "without" : expiration;
  const filterKey = JSON.stringify([access.contextKey, search.trim(), status, effectiveExpiration]);
  const [page, setPage] = useState({ filterKey: "", offset: 0 });
  const offset = page.filterKey === filterKey ? page.offset : 0;
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshToken, setRefreshToken] = useState(0);
  const [selected, setSelected] = useState(null);
  const crossTabRefreshToken = useCrossTabDataRefreshToken();
  const queryKey = JSON.stringify([filterKey, offset]);
  const normalizedSearch = search.trim();
  const hasFilters = Boolean(normalizedSearch) || status !== "all";

  useEffect(() => {
    if (!canView || !businessUnitId) {
      setResult(null);
      setError("");
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    setError("");
    const timeout = window.setTimeout(async () => {
      try {
        const response = await axios.get("/api/purchasing/reports/item-last-purchase", {
          ...access.requestConfig(REPORT_VIEW),
          params: { business_unit_id: businessUnitId, search: normalizedSearch, status, expiration: effectiveExpiration, limit: PAGE_SIZE, offset },
          signal: controller.signal,
        });
        if (!controller.signal.aborted && access.can(REPORT_VIEW, true)) {
          setExpirationScope({ contextKey: access.contextKey, value: response.data?.expirationScope });
          const rowCount = Number(response.data?.summary?.rowCount || 0);
          if (offset > 0 && rowCount <= offset) {
            setPage({ filterKey, offset: Math.max(0, Math.floor((rowCount - 1) / PAGE_SIZE) * PAGE_SIZE) });
          } else {
            setResult({ queryKey, data: response.data });
          }
        }
      } catch (requestError) {
        if (controller.signal.aborted) return;
        setError(requestError?.response?.status === 403
          ? "You do not have access to this report."
          : "Item last purchase report could not be loaded.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 180);
    return () => { window.clearTimeout(timeout); controller.abort(); };
  }, [businessUnitId, canView, normalizedSearch, status, effectiveExpiration, offset, filterKey, queryKey, refreshToken, crossTabRefreshToken, access]);

  useEffect(() => { setSelected(null); }, [businessUnitId, canViewMovements, canView]);

  const current = result?.queryKey === queryKey && !error;
  const rows = current && Array.isArray(result?.data?.rows) ? result.data.rows : [];
  const summary = current ? result?.data?.summary || {} : {};
  const total = Number(summary.rowCount || 0);
  const expirationAsOf = current ? purchaseDate(result?.data?.expirationAsOf, locale) : null;
  const waiting = loading || (!current && !error);
  const changePage = (nextOffset) => setPage({ filterKey, offset: nextOffset });
  const refreshAvailability = () => {
    setResult(null);
    setRefreshToken((value) => value + 1);
  };

  return (
    <div className="pilcore-purchasing-ui ipr-page ilp-page" data-theme={themeId}>
      <div className="ipr-shell">
        <header className="ipr-header">
          <div>
            <span className="ipr-eyebrow"><FiBarChart2 aria-hidden="true" /> {t("Reports")}</span>
            <h1 className="ipr-title">{t("Item last purchase")}</h1>
            <p className="ipr-subtitle">{t("Prioritize stock with an expiration date and review purchases with no known date.")}</p>
          </div>
          <button type="button" className="ipr-button" disabled={loading || !canView || !businessUnitId}
            onClick={() => setRefreshToken((value) => value + 1)}>
            <FiRefreshCw aria-hidden="true" /> {t("Refresh")}
          </button>
        </header>
        {!canView || !businessUnitId ? (
          <p className="ipr-error" role="alert">{t(!businessUnitId ? "Select a business unit to view this report." : "You do not have access to this report.")}</p>
        ) : <>
          <section className="ipr-toolbar" aria-label={t("Item last purchase filters")}>
            <label className="ilp-search">
              <FiSearch aria-hidden="true" />
              <input type="search" value={search} maxLength={200} aria-label={t("Search items by name or code")}
                placeholder={t("Search items by name or code")}
                onChange={(event) => setSearch(event.target.value)} />
            </label>
            <div className="ilp-stateFilters ilp-expirationFilters" role="group" aria-label={t("Expiration date filter")}>
              {[["with", "With expiration date"], ["without", "Without expiration date"]].map(([value, label]) =>
                <button key={value} type="button" className="ipr-button" aria-pressed={effectiveExpiration === value}
                  disabled={value === "with" && restrictedExpiration}
                  onClick={() => {
                    if (value !== effectiveExpiration) {
                      setPage({ filterKey: "", offset: 0 });
                      setExpiration(value);
                    }
                  }}>{t(label)}</button>)}
            </div>
            <div className="ilp-stateFilters" role="group" aria-label={t("Item status")}>
              {[["all", "All"], ["active", "Active"], ["inactive", "Inactive"]].map(([value, label]) =>
                <button key={value} type="button" className="ipr-button" aria-pressed={status === value}
                  onClick={() => setStatus(value)}>{t(label)}</button>)}
            </div>
          </section>
          <div className="ilp-expirationExplanation">
            <p>{t(restrictedExpiration
              ? "Expiration dates require access to all warehouses. Your permitted purchases are shown from oldest to newest."
              : "Dates refer to remaining stock estimated using FIFO: oldest purchases are consumed first.")}</p>
            <p className="ilp-hint">{t("Last purchase uses the delivery date of verified or closed delivery notes.")}
              {!restrictedExpiration && expirationAsOf && <> {t("Reference date")}: {expirationAsOf}.</>}</p>
          </div>
          {!canViewMovements && <p className="ilp-accessHint">{t("Item movement permission is required to view movements.")}</p>}
          {error && <div className="ipr-error" role="alert"><span>{t(error)}</span>
            <button type="button" className="ipr-button" onClick={() => setRefreshToken((value) => value + 1)}>{t("Retry")}</button>
          </div>}
          <section className="ipr-stats" aria-label={t("Item last purchase summary")}>
            <div className="ipr-stat"><span>{t("Items purchased")}</span><strong>{current ? total.toLocaleString(locale) : "—"}</strong></div>
          </section>
          <section className="ipr-tableWrap" aria-label={t("Items purchased")}>
            <div className="ipr-status">
              <strong>{t(effectiveExpiration === "with" ? "Nearest expiration first" : "Oldest purchase first")}</strong>
              <span role="status">{waiting ? t("Loading...") : error ? "—" :
                `${t("Showing")} ${rows.length ? offset + 1 : 0}–${rows.length ? offset + rows.length : 0} ${t("of")} ${total.toLocaleString(locale)}`}</span>
            </div>
            <div className="ipr-tableScroller" tabIndex={0} aria-label={t("Items purchased")} aria-busy={waiting}>
              {rows.length ? <table className="ipr-table">
                <thead><tr>
                  <th scope="col">{t("Item code")}</th>
                  <th scope="col">{t("Item")}</th>
                  <th scope="col">{t("Category")}</th>
                  <th scope="col" aria-sort={effectiveExpiration === "with" ? "ascending" : undefined}>{t("Stock expiration")}{effectiveExpiration === "with" ? " ↑" : ""}</th>
                  <th scope="col" aria-sort={effectiveExpiration === "without" ? "ascending" : undefined}>{t("Last purchase")}{effectiveExpiration === "without" ? " ↑" : ""}</th>
                  <th scope="col" className="ipr-num">{t("Days since last purchase")}</th>
                  <th scope="col">{t("Movements")}</th>
                  <th scope="col">{t("Status")}</th>
                  {canEditItems && <th scope="col">{t("Availability")}</th>}
                </tr></thead>
                <tbody>{rows.map((row) => {
                  const date = purchaseDate(row.lastPurchaseDate, locale);
                  const days = daysSince(row.lastPurchaseDate);
                  const availability = {
                    managed_by_admin: row.managedByAdmin, corporate_catalog_id: row.corporateCatalogId,
                    merged_into_item_id: row.mergedIntoItemId,
                  };
                  return <tr key={row.itemId}>
                    <td>{row.itemCode || "—"}</td>
                    <td className="ipr-mainCell">{row.itemName || "—"}</td>
                    <td>{row.categoryName || "—"}</td>
                    <td><StockExpiration row={row} restricted={restrictedExpiration} locale={locale} t={t} /></td>
                    <td className="ilp-date">{date
                      ? <time dateTime={String(row.lastPurchaseDate).slice(0, 10)}>{date}</time>
                      : "—"}</td>
                    <td className="ipr-num">{days === null ? "—" : days.toLocaleString(locale)}</td>
                    <td><button type="button" className="ipr-button" aria-haspopup="dialog"
                      aria-label={`${t("View movements")}: ${row.itemName || row.itemCode}`}
                      disabled={!canViewMovements}
                      onClick={() => setSelected({ businessUnitId, row })}>
                      <FiEye aria-hidden="true" /> {t("View")}
                    </button></td>
                    <td><ItemAvailabilityStatus is_active={row.isActive} /></td>
                    {canEditItems && <td><ItemAvailabilityControl
                      key={`${businessUnitId}:${row.itemId}`}
                      itemId={row.itemId} itemName={row.itemName || row.itemCode}
                      is_active={row.isActive} businessUnitId={businessUnitId}
                      canEdit={canEditItems} locked={isItemAvailabilityLocked(availability)}
                      showStatus={false} onUpdated={refreshAvailability} /></td>}
                  </tr>;
                })}</tbody>
              </table> : <div className="ipr-empty" role="status">
                <FiBarChart2 size={28} aria-hidden="true" />
                <strong>{t(waiting ? "Loading items..." : error || (hasFilters
                  ? "No purchased items match the current filters."
                  : effectiveExpiration === "with" ? "No stock with a recorded expiration date." : "No purchased items without a known expiration date."))}</strong>
                {!waiting && !error && hasFilters && <span>{t("Try another search or change the filters.")}</span>}
              </div>}
            </div>
            <nav className="ilp-pagination" aria-label={t("Report pages")}>
              <button type="button" className="ipr-button" disabled={waiting || offset === 0}
                onClick={() => changePage(Math.max(0, offset - PAGE_SIZE))}><FiArrowLeft aria-hidden="true" />{t("Previous")}</button>
              <button type="button" className="ipr-button" disabled={waiting || Boolean(error) || offset + rows.length >= total}
                onClick={() => changePage(offset + PAGE_SIZE)}>{t("Next")}<FiArrowRight aria-hidden="true" /></button>
            </nav>
          </section>
        </>}
      </div>
      {canView && canViewMovements && selected?.businessUnitId === businessUnitId &&
        <ItemMovementHistory key={`${businessUnitId}:${selected.row.itemId}`} row={selected.row}
          businessUnitId={businessUnitId} onClose={() => setSelected(null)}
          onDataChanged={() => setRefreshToken((value) => value + 1)} />}
    </div>
  );
}
